export default customElements.define('w-investment-style', class InvestmentStyle extends wealthify.CustomElementBaseClass {

    constructor () {
        const styleSelector = 'c-investment-style';
        const config = {
            styleSelector,
            templateHTML: `
                <w-fancy-radio-list data-root>
                    <p class="${styleSelector}__introduction" data-introduction></p>
                    <w-modal heading="Our Investment Styles" data-modal>
                        <p>By carefully balancing the type of investments in your Plan, we can tailor the amount of risk you take when investing - Cautious is our lowest risk Plan, while Adventurous is our highest risk Plan.</p>
                        <w-accordion-item key="style-accordion-1" titleprop="Cautious">
                            <p>This Plan is all about minimising losses with the aim of beating inflation. It offers more potential than sticking your money in a bank, although you’re unlikely to see the same return as our more adventurous Plans. This is a Plan designed to ‘stay the course’ with small movements up and down to be expected.</p>
                        </w-accordion-item>
                        <w-accordion-item key="style-accordion-2" titleprop="Tentative">
                            <p>The priority here is to limit loss, but still aim to achieve reasonable gains. There will be a larger number of shares held than in our Cautious Plans, but with more stable investments like Cash and Bonds helping to reduce any volatility within the markets. However, taking more risk for improved gains means that you also need to be comfortable with potentially seeing some downward movement in your Plans when markets change.</p>
                        </w-accordion-item>
                        <w-accordion-item key="style-accordion-3" titleprop="Confident">
                            <p>This Plan is a bit of a balancing act between making gains and limiting loses. It’s the most popular choice with our customers who are looking to make potential gains from their investments and who are willing to take a risk that they may see their investments go down as well.</p>
                        </w-accordion-item>
                        <w-accordion-item key="style-accordion-4" titleprop="Ambitious">
                            <p>This Plan was created for investors whose main priority is to make decent-sized gains, with the outlook that they’re happy to endure the ups and downs of investing. In order to have the potential of making larger gains, you need to take larger risks which can mean when times are hard you may see big losses as well.</p>
                        </w-accordion-item>
                        <w-accordion-item key="style-accordion-5" titleprop="Adventurous">
                            <p>Go big or go home is our Adventurous Plan in a nutshell. With our boldest Plan, we invest with even higher risk in order to maximise the potential returns. But this risk can go both ways, so in times of market downfall you’re likely to see significant bumps.</p>
                        </w-accordion-item>
                    </w-modal>
                </w-fancy-radio-list>`,
            cacheDom: [ 'data-introduction', 'data-modal' ]
        };

        super(config);

        this.dom.root.items = this._getDefaultItems();
        this.dom.root.buttondata = this._getDefaultButtonData();
        this.dom.root.shapemobile = 'diamondsmall';

        this.addEventListener(this.events.ValueChange.name, () => {
            this.value = this.dom.root.value;
            this.valid = this.dom.root.valid;
            this.nameValue = this._getSelectedStyle();
        });

        this.onConnectedCallback = () => {
            this.style.display = 'block';
        };
    }

    static eventSettings = Object.freeze({events: {ValueChange: 'valuechange'}});
    static defaultHeading = 'Choose an investment style';
    static defaultIntroduction = 'By carefully balancing the type of investments in your Plan, we can tailor the amount of risk you take when investing - Cautious is our lowest risk Plan, while Adventurous is our highest risk Plan.';
    static defaultKey = 'RiskValue';

    static get observedProps () {

        return [
            new wealthify.Prop('disabled-items', {
                default: [],
                type: wealthify.propTypes.Array(),
                validators: [wealthify.propValidators.IsInArray(Object.values(wealthify.enums.riskValue))]
            }),
            new wealthify.Prop('heading', {default: this.defaultHeading}),
            new wealthify.Prop('headinglarge', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('introduction', {default: this.defaultIntroduction}),
            new wealthify.Prop('key', {default: this.defaultKey}),
            new wealthify.Prop('mobileview', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('selected', {
                default: 2,
                type: wealthify.propTypes.Number(),
                validators: [wealthify.propValidators.IsPositiveNumber()]
            }),
            new wealthify.Prop('simplified', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            wealthify.props.DarkMode()
        ];
    }

    get propCallbacks () {
        return {
            disabledItems: (value) => this.dom.root.disabledItems = value,
            heading: () => {
                this.dom.root.titleprop = this.heading;
            },
            headinglarge: () => {
                this.dom.root.headinglarge = this.headinglarge;
            },
            introduction: (value) => {
                this.dom.introduction.innerHTML = value;
            },
            key: () => {
                this.dom.root.key = this.key;
            },
            mobileview: () => {
                this.dom.root.mobileview = this.mobileview;
            },
            selected: (value) => this.dom.root.selected = value,
            simplified: (value) => {
                const classListAction = value ? 'add' : 'remove';
                const buttonData = value ? {} : this._getDefaultButtonData();

                this.dom.introduction.classList[classListAction]('u-display__none');
                this.dom.root.buttondata = buttonData;
            },
        };
    }

    _getDefaultButtonData () {
        return {
            label: 'More about our styles',
            onclickprop: (() => {
                this.dom.modal.open = true;

            }).bind(this)
        };
    }

    _getDefaultItems () {
        return [
            {
                name: 'Cautious',
                label: 'Cautious',
                labelmobile: '1',
                value: wealthify.enums.riskValue.One,
                description: 'Minimising loss is the priority. Small movements up and down in Plan value are acceptable, with the aim of beating inflation.',
                descriptionMobile: '<strong>Cautious</strong> - Minimising loss is the priority. Small movements up and down in Plan value are acceptable, with the aim of beating inflation.',
            },
            {
                name: 'Tentative',
                label: 'Tentative',
                labelmobile: '2',
                value: wealthify.enums.riskValue.Two,
                description: 'Limiting loss is important. Moderate movements up and down in Plan value are acceptable, with the aim of achieving reasonable growth.',
                descriptionMobile: '<strong>Tentative</strong> - Limiting loss is important. Moderate movements up and down in Plan value are acceptable, with the aim of achieving reasonable growth.',
            },
            {
                name: 'Confident',
                label: 'Confident',
                labelmobile: '3',
                value: wealthify.enums.riskValue.Three,
                description: 'Minimising losses is as important as making gains. Movements up and down in Plan value are acceptable, with the aim of achieving good growth.',
                descriptionMobile: '<strong>Confident</strong> - Minimising losses is as important as making gains. Movements up and down in Plan value are acceptable, with the aim of achieving good growth.',
            },
            {
                name: 'Ambitious',
                label: 'Ambitious',
                labelmobile: '4',
                value: wealthify.enums.riskValue.Four,
                description: 'Making gains is the priority. The risk of large losses and large movements up and down in Plan value are acceptable, with the aim of achieving high growth.',
                descriptionMobile: '<strong>Ambitious</strong> - Making gains is the priority. The risk of large losses and large movements up and down in Plan value are acceptable, with the aim of achieving high growth.',
            },
            {
                name: 'Adventurous',
                label: 'Adventurous',
                labelmobile: '5',
                value: wealthify.enums.riskValue.Five,
                description: 'Maximising returns is the priority. The risk of substantial losses and substantial movements up and down in Plan value are acceptable, with the aim of achieving the highest growth possible.',
                descriptionMobile: '<strong>Adventurous</strong> - Maximising returns is the priority. The risk of substantial losses and substantial movements up and down in Plan value are acceptable, with the aim of achieving the highest growth possible.',
            }
        ];
    }

    _getSelectedStyle () {
        return this.value ? this._getDefaultItems()[this.value - 1].name : null;
    }

});